import axios from "axios";
import { APIClient } from "../helpers/api_helper";
import {
  ApiResponse,
  PaginatedApiResponse,
  PaginationParams,
} from "../types/api";
import {
  ClientRecordTemplate,
  ClientRecordTemplateSchema,
  isAnamnesisAnswerableFieldType,
  normalizeSchemaFieldType,
  SchemaFieldDefinition,
  SchemaFieldType,
} from "./clientRecordTemplateService";

export {
  ANAMNESIS_TEXTAREA_ALIASES,
  ANAMNESIS_TEXTAREA_BUILDER_ROWS,
  ANAMNESIS_TEXTAREA_CHAR_COUNT_THRESHOLD,
  ANAMNESIS_TEXTAREA_FILL_ROWS,
  ANAMNESIS_TEXTAREA_MAX_CHARS,
  isAnamnesisAnswerableFieldType,
  normalizeSchemaFieldType,
} from "./clientRecordTemplateService";
export type { SchemaFieldType };

const api = new APIClient();

const clientsResource = "/clients";

export interface ClientRecordProfessional {
  id: number;
  name: string;
}

export interface ClientRecordAuthor {
  id: number;
  name: string;
}

export type ClientRecordType = "anamnesis" | "evolution";

export type ClientRecordAttachmentType = "before" | "after" | "other";

export type ClientRecordAttachmentStatus = "pending" | "ready" | "failed";

export interface ClientRecordAttachment {
  id: number;
  tenant_id: number;
  client_record_id: number;
  attachment_type: ClientRecordAttachmentType;
  original_filename: string;
  mime_type: string;
  file_size_bytes: number;
  status: ClientRecordAttachmentStatus;
  marketing_ok?: boolean;
  download_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface ClientRecord {
  id: number;
  tenant_id: number;
  client_id: number;
  professional_id: number | null;
  template_id: number | null;
  service_id?: number | null;
  appointment_id?: number | null;
  record_type?: ClientRecordType;
  notes?: string | null;
  attachments_count?: number;
  attachments?: ClientRecordAttachment[];
  answers_json: Record<string, unknown>;
  template?: ClientRecordTemplate;
  professional?: ClientRecordProfessional | null;
  service?: { id: number; name: string; client_record_template_id?: number | null } | null;
  recorded_by?: ClientRecordAuthor | null;
  can_correct?: boolean;
  has_signed_consent?: boolean;
  consent_signed_at?: string | null;
  consent_term_id?: number | null;
  consent_term_version?: number | null;
  consent_term_outdated?: boolean;
  has_establishment_signature?: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateClientRecordPayload {
  record_type?: ClientRecordType;
  template_id?: number | null;
  professional_id?: number | null;
  service_id?: number | null;
  appointment_id?: number | null;
  answers_json?: Record<string, unknown>;
  notes?: string | null;
}

export interface StoreClientRecordConsentPayload {
  consent_text_snapshot?: string;
  signature_image_base64?: string | null;
  establishment_signature_image_base64?: string | null;
  consent_term_id?: number | null;
  signed_at?: string;
  resign?: boolean;
  resign_reason?: string;
}

export interface ClientRecordConsentSummary {
  id: number;
  client_record_id: number;
  client_id: number;
  signed_at: string | null;
  has_signature_image: boolean;
  has_establishment_signature?: boolean;
  has_signed_consent: boolean;
  consent_term_id?: number | null;
  consent_term_version?: number | null;
}

export interface UpdateClientRecordPayload {
  answers_json?: Record<string, unknown>;
  notes?: string | null;
  expected_updated_at?: string;
}

export interface ClientRecordListParams extends PaginationParams {
  q?: string;
  service_id?: number;
  general_only?: boolean;
  record_type?: ClientRecordType;
  appointment_id?: number;
}

export const getRecordsByClient = (
  clientId: number,
  params?: ClientRecordListParams
): Promise<PaginatedApiResponse<ClientRecord>> =>
  api.get<PaginatedApiResponse<ClientRecord>>(
    `${clientsResource}/${clientId}/records`,
    params
  );

export const createRecord = (
  clientId: number,
  payload: CreateClientRecordPayload
): Promise<ApiResponse<ClientRecord>> =>
  api.create<ApiResponse<ClientRecord>>(
    `${clientsResource}/${clientId}/records`,
    payload
  );

export const copyRecordToService = (
  clientId: number,
  recordId: number,
  payload: { service_id: number; professional_id?: number | null }
): Promise<ApiResponse<ClientRecord>> =>
  api.create<ApiResponse<ClientRecord>>(
    `${clientsResource}/${clientId}/records/${recordId}/copy-to-service`,
    payload
  );

export const getRecord = (recordId: number): Promise<ApiResponse<ClientRecord>> =>
  api.get<ApiResponse<ClientRecord>>(`/client-records/${recordId}`);

export const updateRecord = (
  recordId: number,
  payload: UpdateClientRecordPayload
): Promise<ApiResponse<ClientRecord>> =>
  api.put<ApiResponse<ClientRecord>>(`/client-records/${recordId}`, payload);

export const uploadAttachment = async (
  recordId: number,
  file: File,
  attachmentType: ClientRecordAttachmentType
): Promise<ApiResponse<ClientRecordAttachment>> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("attachment_type", attachmentType);

  return axios.post<ApiResponse<ClientRecordAttachment>>(
    `/client-records/${recordId}/attachments`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  ) as unknown as Promise<ApiResponse<ClientRecordAttachment>>;
};

export const updateAttachmentMarketingOk = (
  recordId: number,
  attachmentId: number,
  marketingOk: boolean
): Promise<ApiResponse<ClientRecordAttachment>> =>
  api.update<ApiResponse<ClientRecordAttachment>>(
    `/client-records/${recordId}/attachments/${attachmentId}/marketing-ok`,
    { marketing_ok: marketingOk }
  );

export const deleteAttachment = (
  recordId: number,
  attachmentId: number
): Promise<ApiResponse<Record<string, never>>> =>
  api.delete<ApiResponse<Record<string, never>>>(
    `/client-records/${recordId}/attachments/${attachmentId}`
  );

export const downloadAttachmentBlob = async (
  recordId: number,
  attachmentId: number
): Promise<Blob> => {
  const blob = await axios.get<Blob>(
    `/client-records/${recordId}/attachments/${attachmentId}/download`,
    { responseType: "blob" }
  );

  return blob as unknown as Blob;
};

export const storeConsent = (
  recordId: number,
  payload: StoreClientRecordConsentPayload
): Promise<ApiResponse<ClientRecordConsentSummary>> =>
  api.create<ApiResponse<ClientRecordConsentSummary>>(
    `/client-records/${recordId}/consents`,
    payload
  );

export const exportRecordPdf = async (
  clientId: number,
  recordId: number
): Promise<Blob> => {
  const blob = await axios.get<Blob>(
    `${clientsResource}/${clientId}/records/${recordId}/export`,
    { responseType: "blob" }
  );

  return blob as unknown as Blob;
};

export const exportRecordsBatchPdf = async (
  clientId: number,
  recordIds: number[]
): Promise<Blob> => {
  const blob = await axios.post<Blob>(
    `${clientsResource}/${clientId}/records/export-batch`,
    { record_ids: recordIds },
    { responseType: "blob" }
  );

  return blob as unknown as Blob;
};

export type ParsedSchemaField = {
  name: string;
  label: string;
  type: SchemaFieldType;
  options: string[];
  required: boolean;
  body: string;
};

export const parseSchemaFields = (
  schemaJson: ClientRecordTemplateSchema | null | undefined
): ParsedSchemaField[] => {
  if (!schemaJson) {
    return [];
  }

  const rawFields = Array.isArray(schemaJson)
    ? schemaJson
    : schemaJson.fields ?? [];

  if (!Array.isArray(rawFields)) {
    return [];
  }

  return rawFields
    .map((field: SchemaFieldDefinition) => {
      const name = String(field.slug ?? field.name ?? "").trim();
      const label = String(field.label ?? name).trim();
      const type = normalizeSchemaFieldType(field.type);

      let options: string[] = [];

      if (Array.isArray(field.options)) {
        options = field.options.map((option) => {
          if (typeof option === "string") {
            return option;
          }

          return String(option.value ?? option.label ?? "");
        });
      }

      return {
        name,
        label,
        type,
        options,
        required: Boolean(field.required) && isAnamnesisAnswerableFieldType(type),
        body: String(field.body ?? "").trim(),
      };
    })
    .filter((field) => field.name.length > 0);
};

export const collectConsentText = (fields: ParsedSchemaField[]): string =>
  fields
    .filter((field) => field.type === "consent_text" && field.body.length > 0)
    .map((field) => field.body)
    .join("\n\n");

export const templateRequiresConsent = (fields: ParsedSchemaField[]): boolean =>
  fields.some((field) =>
    ["consent_text", "consent_checkbox", "signature"].includes(field.type)
  );